export type TaxFramework = 'AY_2026_27_1961' | 'TAX_YEAR_2026_27_2025_ACT';

export interface FrameworkInfo {
  id: TaxFramework;
  label: string;
  actName: string;
  yearLabel: string;
  description: string;
}

export const FRAMEWORKS: Record<TaxFramework, FrameworkInfo> = {
  AY_2026_27_1961: {
    id: 'AY_2026_27_1961',
    label: 'AY 2026–27 (FY 2025–26)',
    actName: 'Income-tax Act, 1961 (Transition)',
    yearLabel: 'AY 2026-27',
    description: 'Assessment year under the existing 1961 Act provisions.'
  },
  TAX_YEAR_2026_27_2025_ACT: {
    id: 'TAX_YEAR_2026_27_2025_ACT',
    label: 'Tax Year 2026–27 (New 2025 Act)',
    actName: 'Income-tax Act, 2025',
    yearLabel: 'Tax Year 2026-27',
    description: 'Simplified structure under the newly enacted Income-tax Act, 2025.'
  }
};

// Section Mapping Entry
export interface SectionMapping {
  oldSection: string;
  newSection: string;
  topic: string;
  oldDetails: string;
  newDetails: string;
  effectiveYear: string;
  applicableTo: string;
  actionToolId?: string;
}

export const SECTION_MAPPINGS: SectionMapping[] = [
  {
    oldSection: '80C',
    newSection: 'Section 123 / Schedule XV',
    topic: 'Specified Deductions (PPF, EPF, ELSS, Tuition Fee, LIC)',
    oldDetails: 'Deduction up to ₹1,50,000 for specified investments under Chapter VI-A.',
    newDetails: 'Consolidated under Schedule XV provisions with simplified compliance limits.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Individuals & HUFs',
    actionToolId: '80c-calculator'
  },
  {
    oldSection: '80D',
    newSection: 'Schedule XV (Health)',
    topic: 'Medical Insurance Premium & Preventive Health Checkup',
    oldDetails: 'Deduction up to ₹25,000 for self/family and ₹50,000 for senior citizens.',
    newDetails: 'Streamlined healthcare deduction schedule under 2025 Act.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Individuals & HUFs',
    actionToolId: '80d-calculator'
  },
  {
    oldSection: '24(b)',
    newSection: 'Section 42 / House Property Schedule',
    topic: 'Deduction for Home Loan Interest (Self-Occupied & Rented)',
    oldDetails: 'Deduction up to ₹2,00,000 on home loan interest for self-occupied property.',
    newDetails: 'Unified interest allowance classification under Income-tax Act, 2025.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Home Loan Borrowers',
    actionToolId: 'homeloan-calculator'
  },
  {
    oldSection: '87A',
    newSection: 'Section 112 Rebate',
    topic: 'Tax Rebate for Resident Individuals',
    oldDetails: 'Full rebate up to ₹25,000 for taxable income up to ₹7,00,000 under New Regime.',
    newDetails: 'Unified income threshold rebate provision with marginal relief calculation.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Resident Individuals',
    actionToolId: 'tax-calculator'
  },
  {
    oldSection: '10(13A)',
    newSection: 'Section 28 / Salary Schedule',
    topic: 'House Rent Allowance (HRA) Exemption',
    oldDetails: 'Exemption based on least of Actual HRA, Rent paid - 10% Salary, or 50%/40% Metro cap.',
    newDetails: 'Direct salary offset calculation under 2025 Act rules for Old Regime option.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Salaried Employees',
    actionToolId: 'hra-calculator'
  },
  {
    oldSection: '115BAC',
    newSection: 'Standard Default Regime (Act 2025)',
    topic: 'Concessional Tax Rate Regime',
    oldDetails: 'Default tax regime with revised slabs and ₹75,000 standard deduction.',
    newDetails: 'Primary tax framework under Income-tax Act, 2025.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'All Individual Taxpayers',
    actionToolId: 'old-vs-new'
  },
  {
    oldSection: '44AD / 44ADA',
    newSection: 'Section 65 Presumptive Income',
    topic: 'Presumptive Taxation for Small Businesses & Professionals',
    oldDetails: 'Presumptive profit @ 6%/8% (44AD) or 50% (44ADA) up to turnover thresholds.',
    newDetails: 'Harmonized small business & freelancer simplified declaration rules.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Small Businesses & Freelancers',
    actionToolId: 'business-tax'
  },
  {
    oldSection: '115BBH',
    newSection: 'Section 148 VDA Tax',
    topic: 'Tax on Virtual Digital Assets (Crypto / NFTs)',
    oldDetails: 'Flat 30% tax on gains with 1% TDS under 194S without loss set-off.',
    newDetails: 'Dedicated Digital Asset income classification under 2025 Act.',
    effectiveYear: 'Tax Year 2026–27',
    applicableTo: 'Crypto Traders & VDA Holders',
    actionToolId: 'crypto-tax'
  }
];

// AY / Tax Year Converter Data
export interface AyConversionResult {
  input: string;
  assessmentYear: string;
  financialYear: string;
  taxYear2025Act: string;
  governingAct: string;
  notes: string;
}

export function convertTaxYear(inputYear: string): AyConversionResult {
  const clean = inputYear.trim().toUpperCase();
  if (clean.includes('2026') || clean.includes('26-27')) {
    return {
      input: inputYear,
      assessmentYear: 'AY 2026–27',
      financialYear: 'FY 2025–26',
      taxYear2025Act: 'Tax Year 2026–27',
      governingAct: 'Income-tax Act, 2025 (Transitional Framework)',
      notes: 'AY 2026–27 connects the 1961 Act framework to the newly enacted Income-tax Act, 2025.'
    };
  } else if (clean.includes('2025') || clean.includes('25-26')) {
    return {
      input: inputYear,
      assessmentYear: 'AY 2025–26',
      financialYear: 'FY 2024–25',
      taxYear2025Act: 'N/A (Pre-2025 Act)',
      governingAct: 'Income-tax Act, 1961',
      notes: 'Governed strictly by the Income-tax Act, 1961 provisions, Finance Act 2024.'
    };
  } else {
    return {
      input: inputYear,
      assessmentYear: `AY ${clean}`,
      financialYear: `FY ${clean}`,
      taxYear2025Act: `Tax Year ${clean}`,
      governingAct: 'Income-tax Act (Applicable Period Framework)',
      notes: 'Fingard automatically maps this input to the relevant statutory provisions.'
    };
  }
}

// Income Tax Calculation Engine (1961 vs 2025 Act)
export interface TaxCalculationInput {
  taxpayerType: 'individual_below_60' | 'senior_60_80' | 'super_senior_80';
  framework: TaxFramework;
  salaryIncome: number;
  businessIncome: number;
  housePropertyIncome: number;
  capitalGains: number;
  otherIncome: number;
  deductions80C: number;
  deductions80D: number;
  otherDeductions: number;
}

export interface TaxCalculationResult {
  grossIncome: number;
  totalDeductions: number;
  taxableIncome: number;
  basicTax: number;
  rebate87A: number;
  cess: number;
  totalTaxPayable: number;
  effectiveRegime: string;
  governingAct: string;
  breakdownNotes: string[];
}

export function calculateIncomeTax(input: TaxCalculationInput): TaxCalculationResult {
  const gross = input.salaryIncome + input.businessIncome + input.housePropertyIncome + input.capitalGains + input.otherIncome;
  
  // Standard Deduction of ₹75,000 for salaried under New Regime / 2025 Act
  const stdDeduction = input.salaryIncome > 0 ? 75000 : 0;
  
  let totalDeductionAllowed = 0;
  let taxable = gross;
  let basicTax = 0;
  let rebate = 0;
  
  if (input.framework === 'TAX_YEAR_2026_27_2025_ACT' || input.framework === 'AY_2026_27_1961') {
    // New 2025 Act / New Regime Slabs
    totalDeductionAllowed = stdDeduction;
    taxable = Math.max(0, gross - totalDeductionAllowed);

    if (taxable <= 400000) {
      basicTax = 0;
    } else if (taxable <= 800000) {
      basicTax = (taxable - 400000) * 0.05;
    } else if (taxable <= 1200000) {
      basicTax = (400000 * 0.05) + ((taxable - 800000) * 0.10);
    } else if (taxable <= 1600000) {
      basicTax = (400000 * 0.05) + (400000 * 0.10) + ((taxable - 1200000) * 0.15);
    } else if (taxable <= 2000000) {
      basicTax = (400000 * 0.05) + (400000 * 0.10) + (400000 * 0.15) + ((taxable - 1600000) * 0.20);
    } else if (taxable <= 2400000) {
      basicTax = (400000 * 0.05) + (400000 * 0.10) + (400000 * 0.15) + (400000 * 0.20) + ((taxable - 2000000) * 0.25);
    } else {
      basicTax = (400000 * 0.05) + (400000 * 0.10) + (400000 * 0.15) + (400000 * 0.20) + (400000 * 0.25) + ((taxable - 2400000) * 0.30);
    }

    // Section 87A Rebate for Taxable Income up to 7 Lakhs (or 12 Lakhs under 2025 Act proposal)
    if (taxable <= 700000) {
      rebate = Math.min(basicTax, 25000);
    }
  } else {
    // AY 2025-26 Old Act calculation option
    const claimedDeductions = Math.min(150000, input.deductions80C) + Math.min(50000, input.deductions80D) + input.otherDeductions + stdDeduction;
    totalDeductionAllowed = claimedDeductions;
    taxable = Math.max(0, gross - totalDeductionAllowed);

    if (taxable <= 250000) {
      basicTax = 0;
    } else if (taxable <= 500000) {
      basicTax = (taxable - 250000) * 0.05;
    } else if (taxable <= 1000000) {
      basicTax = (250000 * 0.05) + ((taxable - 500000) * 0.20);
    } else {
      basicTax = (250000 * 0.05) + (500000 * 0.20) + ((taxable - 1000000) * 0.30);
    }

    if (taxable <= 500000) {
      rebate = Math.min(basicTax, 12500);
    }
  }

  const taxAfterRebate = Math.max(0, basicTax - rebate);
  const cess = taxAfterRebate * 0.04;
  const totalTaxPayable = Math.round(taxAfterRebate + cess);

  return {
    grossIncome: gross,
    totalDeductions: totalDeductionAllowed,
    taxableIncome: taxable,
    basicTax: basicTax,
    rebate87A: rebate,
    cess: cess,
    totalTaxPayable: totalTaxPayable,
    effectiveRegime: input.framework === 'TAX_YEAR_2026_27_2025_ACT' ? 'Income-tax Act, 2025 Framework' : 'Income-tax Act, 1961 Framework',
    governingAct: FRAMEWORKS[input.framework].actName,
    breakdownNotes: [
      `Standard Deduction applied: ₹${stdDeduction.toLocaleString('en-IN')}`,
      `Governing Law: ${FRAMEWORKS[input.framework].actName}`,
      `Health & Education Cess (4%): ₹${Math.round(cess).toLocaleString('en-IN')}`
    ]
  };
}
